import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, CalendarPlus, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useI18n, formatTime12 } from "@/lib/i18n";

export default function BookingConfirmation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const groupId = params.get("group");
  const { t } = useI18n();
  const [booking, setBooking] = useState(null);

  useEffect(() => { api.get(`/public/booking/${id}`).then(({ data }) => setBooking(data)); }, [id]);

  if (!booking) return <div className="min-h-screen flex items-center justify-center text-zen-muted">Loading…</div>;

  const startDate = new Date(`${booking.date}T${booking.start_time}:00`);
  const endDate = new Date(startDate.getTime() + booking.duration_minutes * 60000);
  // Local (floating) datetime for ICS, formatted as YYYYMMDDTHHMMSS — Apple
  // Calendar interprets this as the device's local time, which matches how
  // the customer sees the appointment.
  const fmtLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) + "T" +
      pad(d.getHours()) +
      pad(d.getMinutes()) +
      pad(d.getSeconds())
    );
  };
  const fmtUTC = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const title = `${booking.services.map((s) => s.name).join(", ")} — Zen Nails Lounge`;
  const description = `Stylist: ${booking.staff_name}\\nBooking: ${booking.id}`;
  const icsLines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Zen Nails Lounge//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${booking.id}@zennail`,
    `DTSTAMP:${fmtUTC(new Date())}`,
    `DTSTART;TZID=America/New_York:${fmtLocal(startDate)}`,
    `DTEND;TZID=America/New_York:${fmtLocal(endDate)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    "LOCATION:Zen Nails Lounge & Spa, Florida",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  const icsContent = icsLines.join("\r\n");
  const icsHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;

  return (
    <div className="min-h-screen bg-zen-bg flex items-center justify-center px-6 py-16" data-testid="booking-confirmation">
      <div className="max-w-xl w-full">
        <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-zen-muted mb-6">
          <div className="w-8 h-[1px] bg-zen-primary" /> Zen Nails Lounge & Spa
        </div>
        <div className="w-14 h-14 rounded-full bg-zen-surface flex items-center justify-center mb-6">
          <Check className="w-6 h-6 text-zen-primary" strokeWidth={1.5} />
        </div>
        <h1 className="font-serif text-5xl lg:text-6xl text-zen-ink leading-[1.05] mb-3">{t("booking_confirmed")}</h1>
        <p className="text-zen-muted mb-12">{t("booking_confirmed_msg")}</p>
        <div className="bg-white rounded-2xl border border-zen-border p-8 space-y-5 mb-8" data-testid="confirmation-details">
          <Row label={t("booking_id")} value={booking.id.slice(0, 8).toUpperCase()} />
          <Row label={t("services")} value={booking.services.map((s) => s.name).join(", ")} />
          <Row label={t("stylist")} value={booking.staff_name} />
          <Row label={t("date")} value={new Date(booking.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} />
          <Row label={t("time")} value={`${formatTime12(booking.start_time)} – ${formatTime12(booking.end_time)}`} />
          {groupId && <Row label="Group" value={groupId.slice(0, 8).toUpperCase()} />}
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button asChild variant="outline" className="rounded-full" data-testid="add-to-calendar-btn">
            <a href={icsHref} download={`zen-nail-${booking.id.slice(0,8)}.ics`}><CalendarPlus className="w-4 h-4 mr-2" /> {t("add_to_calendar")}</a>
          </Button>
          <Button asChild className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white" data-testid="new-booking-btn">
            <Link to="/"><Sparkles className="w-4 h-4 mr-2" /> {t("new_booking")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-zen-muted">{label}</span>
      <span className="text-zen-ink text-right font-medium">{value}</span>
    </div>
  );
}
